import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@shared/services/auth.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { GlobalProgressSpinnerService } from '@shared/services/global-progress-spinner.service';
import { PasswordModule } from 'primeng/password';
import { FluidModule } from 'primeng/fluid';
import { Registration } from '../registration.model';
import { RegistrationService } from '../registration.service';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-generic-details-form',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    ReactiveFormsModule,
    FluidModule,
  ],
  templateUrl: './generic-details-form.component.html',
  styleUrl: './generic-details-form.component.css',
})
export class GenericDetailsFormComponent {
  readonly form: FormGroup;

  private readonly _globalProgressSpinnerService = inject(
    GlobalProgressSpinnerService
  );
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _registrationService = inject(RegistrationService);
  private readonly _messageService = inject(MessageService);
  private readonly _authService = inject(AuthService);

  get f() {
    return this.form.controls;
  }
  constructor() {
    this.form = this._formBuilder.group({
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      countryCode: ['+30', Validators.required],
      mobile: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    const fullName = this.form.value.fullName;
    const email = this.form.value.email;
    const countryCode = this.form.value.countryCode;
    const mobile = this.form.value.mobile;
    const password = this.form.value.password;

    const registrationData: Registration = {
      fullName,
      email,
      mobile: `${countryCode}${mobile}`,
      password,
    };

    this._globalProgressSpinnerService.showLoader();

    try {
      await this._registrationService.registerUser(registrationData);
      this._registrationService.nextRegistrationStep();
    } catch (error: any) {
      if (
        error.message.includes(
          'A user with the same id, email, or phone already exists in this project.'
        )
      ) {
        error.message =
          'A user with this email or phone number already exists. Please try again.';
      }

      this._messageService.add({
        severity: 'error',
        summary: error.message,
      });
    } finally {
      this._globalProgressSpinnerService.hideLoader();
      await this._authService.logout();
    }
  }
}
